import { useEffect, useState } from 'react';
import type { AuditRecord, LeadRecord, OutreachRecord, ProposalRecord, QaRunRecord } from '@360t/shared';
import { api, type OutreachDraft } from '../lib/api';
import { OpportunityBadge } from './OpportunityBadge';

const SOCIAL_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'X / Twitter',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
};

const PACKAGE_LABEL: Record<string, string> = {
  new_website: 'New website build',
  revamp: 'Website revamp',
  maintenance: 'Care plan (monthly)',
  seo_aeo: 'SEO & AEO programme',
  automation: 'Business automation',
  bundle: 'Website + growth bundle',
};

export function LeadDrawer({
  leadId,
  onClose,
  onChanged,
}: {
  leadId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [lead, setLead] = useState<LeadRecord | null>(null);
  const [audit, setAudit] = useState<AuditRecord | null>(null);
  const [qaRun, setQaRun] = useState<QaRunRecord | null>(null);
  const [proposals, setProposals] = useState<ProposalRecord[]>([]);
  const [outreach, setOutreach] = useState<OutreachRecord[]>([]);
  const [draft, setDraft] = useState<OutreachDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [kind, setKind] = useState<string>('');

  const reload = async () => {
    const [l, p, o] = await Promise.all([
      api.getLead(leadId),
      api.listProposals(leadId),
      api.listOutreach(leadId),
    ]);
    setLead(l);
    setProposals(p);
    setOutreach(o);

    // Findings live on the run, not the lead, so pull the latest of each.
    const [a, q] = await Promise.all([
      l.latestAudit ? api.getAudit(l.latestAudit.id) : Promise.resolve(null),
      l.latestQaRun ? api.getQaRun(l.latestQaRun.id) : Promise.resolve(null),
    ]);
    setAudit(a);
    setQaRun(q);
  };

  /**
   * Mark a finding as a false positive (or undo it). Optimistic like the
   * pipeline board: flip it locally, roll back if the API refuses. Dismissing
   * annotates the finding only — it does not remove it or change any score.
   */
  const dismissIssue = async (issueId: string, dismissed: boolean) => {
    if (!audit) return;
    const previous = audit;
    setAudit({
      ...audit,
      issues: audit.issues.map((i) => (i.id === issueId ? { ...i, dismissedAsFalsePositive: dismissed } : i)),
    });
    try {
      await api.dismissAuditIssue(audit.id, issueId, dismissed);
    } catch {
      setAudit(previous);
    }
  };

  const dismissDefect = async (defectId: string, dismissed: boolean) => {
    if (!qaRun) return;
    const previous = qaRun;
    setQaRun({
      ...qaRun,
      defects: qaRun.defects.map((d) => (d.id === defectId ? { ...d, dismissedAsFalsePositive: dismissed } : d)),
    });
    try {
      await api.dismissQaDefect(qaRun.id, defectId, dismissed);
    } catch {
      setQaRun(previous);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setNote(null);
    try {
      await fn();
      await reload();
      onChanged();
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!lead) {
    return (
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-line bg-white p-6 shadow-2xl">
        <p className="text-sm text-slate-400">Loading…</p>
      </aside>
    );
  }

  const socials = lead.socialLinks ?? {};

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-line bg-white shadow-2xl">
        <header className="sticky top-0 flex items-start justify-between gap-4 border-b border-line bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-ink">{lead.businessName}</h2>
            <p className="text-xs text-slate-400">
              {[lead.city, lead.category?.split('=')[1]].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <OpportunityBadge score={lead.opportunityScore} />
            <button onClick={onClose} className="text-slate-400 hover:text-ink" aria-label="Close">
              ✕
            </button>
          </div>
        </header>

        <div className="space-y-6 px-6 py-5">
          {note && <p className="rounded-lg bg-poor/10 px-3 py-2 text-sm text-poor">{note}</p>}

          {/* ── contact ── */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact</h3>
              <button
                className="btn-ghost !px-3 !py-1 text-xs"
                disabled={busy !== null || !lead.websiteUrl}
                onClick={() => void act('enrich', () => api.enrichLead(lead.id))}
              >
                {busy === 'enrich' ? 'Scanning site…' : lead.enrichedAt ? 'Re-scan for contacts' : 'Find contact details'}
              </button>
            </div>
            <dl className="space-y-1.5 text-sm">
              <Row label="Email">
                {lead.email ? (
                  <a href={`mailto:${lead.email}`} className="text-brand-dark hover:underline">
                    {lead.email}
                  </a>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
                {lead.emailConfidence !== null && lead.email && (
                  <span className="ml-2 text-xs text-slate-400">
                    {Math.round(lead.emailConfidence * 100)}% confidence · {lead.emailSource?.replace(/_/g, ' ')}
                  </span>
                )}
              </Row>
              <Row label="Phone">
                {lead.phone ?? <span className="text-slate-300">—</span>}
                {lead.additionalPhones.length > 0 && (
                  <span className="ml-2 text-xs text-slate-400">+{lead.additionalPhones.length} more</span>
                )}
              </Row>
              <Row label="Social">
                {Object.keys(socials).length === 0 ? (
                  <span className="text-slate-300">—</span>
                ) : (
                  <span className="flex flex-wrap gap-2">
                    {Object.entries(socials).map(([k, url]) => (
                      <a
                        key={k}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded border border-line px-2 py-0.5 text-xs hover:border-brand hover:text-brand-dark"
                      >
                        {SOCIAL_LABEL[k] ?? k}
                      </a>
                    ))}
                  </span>
                )}
              </Row>
              {lead.contactSourceUrl && (
                <Row label="Found on">
                  <a href={lead.contactSourceUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:underline">
                    {lead.contactSourceUrl}
                  </a>
                </Row>
              )}
            </dl>
          </section>

          {/* ── site ── */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Website</h3>
              {!lead.websiteUrl && (
                <button
                  className="btn-ghost !px-3 !py-1 text-xs"
                  disabled={busy !== null}
                  onClick={() => void act('verify', () => api.verifyLeadWebsite(lead.id))}
                >
                  {busy === 'verify' ? 'Looking…' : lead.websiteCheckedAt ? 'Check again' : 'Check for a website'}
                </button>
              )}
            </div>
            <dl className="space-y-1.5 text-sm">
              <Row label="URL">
                {lead.websiteUrl ? (
                  <a href={lead.websiteUrl} target="_blank" rel="noreferrer" className="text-brand-dark hover:underline">
                    {lead.websiteUrl}
                  </a>
                ) : lead.websiteCheckedAt ? (
                  <span className="font-bold text-poor">No website found</span>
                ) : (
                  <span className="text-slate-500">
                    None listed — <span className="font-semibold">not checked yet</span>
                  </span>
                )}
                {lead.websiteBroken && <span className="ml-2 font-bold text-poor">· does not load</span>}
              </Row>
              {lead.chainBrand && (
                <Row label="Chain">
                  <span className="text-slate-500">
                    Branch of {lead.chainBrand} — the brand owns the web presence
                  </span>
                </Row>
              )}
              <Row label="Platform">
                {lead.platform ? (
                  <span className="rounded bg-canvas px-2 py-0.5 text-xs font-semibold text-ink">{lead.platform}</span>
                ) : (
                  <span className="text-slate-300">not detected</span>
                )}
              </Row>
              {lead.techStack.length > 0 && (
                <Row label="Stack">
                  <span className="text-xs text-slate-500">{lead.techStack.join(', ')}</span>
                </Row>
              )}
              {lead.revampSignals.length > 0 && (
                <Row label="Revamp signals">
                  <span className="text-xs text-poor">{lead.revampSignals.join(' · ')}</span>
                </Row>
              )}
              <Row label="Scores">
                <span className="text-xs">
                  Audit {lead.latestAudit?.overallScore ?? '—'}/100 · QA {lead.latestQaRun?.qaScore ?? '—'}/100
                </span>
              </Row>
            </dl>
          </section>

          {/* ── findings ── */}
          {(audit?.issues.length || qaRun?.defects.length) ? (
            <section>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Findings</h3>
              <p className="mb-2 text-[11px] text-slate-400">
                Wrong call? Mark it — the finding stays on the report, we just record that you disagreed.
              </p>
              <ul className="space-y-1.5">
                {(audit?.issues ?? []).map((i) => (
                  <FindingRow
                    key={i.id}
                    severity={i.severity}
                    meta={`Audit · ${i.category}`}
                    title={i.title}
                    dismissed={i.dismissedAsFalsePositive}
                    onToggle={() => void dismissIssue(i.id, !i.dismissedAsFalsePositive)}
                  />
                ))}
                {(qaRun?.defects ?? []).map((d) => (
                  <FindingRow
                    key={d.id}
                    severity={d.severity}
                    meta={`QA · ${d.category}`}
                    title={d.description}
                    dismissed={d.dismissedAsFalsePositive}
                    onToggle={() => void dismissDefect(d.id, !d.dismissedAsFalsePositive)}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {/* ── proposals ── */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Proposals</h3>
              <div className="flex items-center gap-2">
                <select
                  className="rounded border border-line px-2 py-1 text-xs"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                >
                  <option value="">Recommend for me</option>
                  {Object.entries(PACKAGE_LABEL).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-primary !px-3 !py-1 text-xs"
                  disabled={busy !== null}
                  onClick={() =>
                    void act('proposal', () =>
                      api.createProposal({ leadId: lead.id, ...(kind ? { kind } : {}) }),
                    )
                  }
                >
                  {busy === 'proposal' ? 'Building…' : 'Generate proposal'}
                </button>
              </div>
            </div>
            {proposals.length === 0 ? (
              <p className="text-sm text-slate-400">None yet.</p>
            ) : (
              <ul className="space-y-2">
                {proposals.map((p) => (
                  <li key={p.id} className="rounded-lg border border-line px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-ink">{PACKAGE_LABEL[p.kind] ?? p.kind}</div>
                        <div className="text-xs text-slate-500">
                          {p.priceOneOff ? `£${p.priceOneOff.toLocaleString()} one-off` : ''}
                          {p.priceOneOff && p.priceMonthly ? ' + ' : ''}
                          {p.priceMonthly ? `£${p.priceMonthly}/mo` : ''} · {p.status}
                        </div>
                      </div>
                      {p.pdfPath && (
                        <a href={p.pdfPath} target="_blank" rel="noreferrer" className="btn-ghost !px-3 !py-1 text-xs">
                          PDF
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── outreach ── */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Outreach</h3>
              <button
                className="btn-ghost !px-3 !py-1 text-xs"
                disabled={busy !== null}
                onClick={() => void act('draft', async () => setDraft(await api.outreachDraft(lead.id)))}
              >
                {busy === 'draft' ? 'Writing…' : 'Draft message'}
              </button>
            </div>

            {draft && (
              <div className="mb-3 rounded-lg border border-line bg-canvas p-3">
                <p className="text-xs text-slate-500">
                  To: <strong className="text-ink">{draft.recipient ?? 'no contact on file'}</strong>
                </p>
                <p className="mt-1 text-sm font-semibold text-ink">{draft.subject}</p>
                <textarea
                  className="mt-2 h-56 w-full rounded border border-line bg-white p-2 font-mono text-xs"
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
                {draft.warnings.map((w) => (
                  <p key={w} className="mt-1 text-xs text-amber-700">
                    {w}
                  </p>
                ))}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    className="btn-ghost !px-3 !py-1 text-xs"
                    onClick={() => void navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`)}
                  >
                    Copy
                  </button>
                  <button
                    className="btn-primary !px-3 !py-1 text-xs"
                    disabled={busy !== null || lead.doNotContact}
                    onClick={() =>
                      void act('log', () =>
                        api.logOutreach({
                          leadId: lead.id,
                          channel: draft.channel,
                          subject: draft.subject,
                          body: draft.body,
                          recipient: draft.recipient ?? undefined,
                          proposalId: proposals[0]?.id,
                          markSent: true,
                        }),
                      )
                    }
                  >
                    I've sent this — log it
                  </button>
                  <span className="text-[11px] text-slate-400">We log sends; we don't send email for you.</span>
                </div>
              </div>
            )}

            {outreach.length === 0 ? (
              <p className="text-sm text-slate-400">Nothing logged yet.</p>
            ) : (
              <ul className="space-y-2">
                {outreach.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">{o.subject ?? o.channel}</div>
                      <div className="text-xs text-slate-400">
                        {o.channel} · {o.sentAt ? new Date(o.sentAt).toLocaleDateString('en-GB') : 'draft'}
                      </div>
                    </div>
                    <select
                      className="rounded border border-line px-2 py-1 text-xs capitalize"
                      value={o.status}
                      onChange={(e) => void act('status', () => api.updateOutreach(o.id, e.target.value))}
                    >
                      {['draft', 'sent', 'opened', 'replied', 'bounced', 'failed'].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

function FindingRow({
  severity,
  meta,
  title,
  dismissed,
  onToggle,
}: {
  severity: 'critical' | 'warning' | 'info';
  meta: string;
  title: string;
  dismissed: boolean;
  onToggle: () => void;
}) {
  return (
    <li
      className={`flex items-start justify-between gap-3 rounded-lg border border-line border-l-[3px] px-3 py-2 ${
        severity === 'critical' ? 'border-l-poor' : severity === 'warning' ? 'border-l-average' : 'border-l-accent'
      } ${dismissed ? 'opacity-50' : ''}`}
    >
      <div className="min-w-0">
        <div className={`text-sm text-ink ${dismissed ? 'line-through' : ''}`}>{title}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{meta}</div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={dismissed}
        className="shrink-0 rounded border border-line px-2 py-0.5 text-[11px] text-slate-500 hover:border-brand hover:text-brand-dark"
      >
        {dismissed ? 'Undo' : 'Not a real issue'}
      </button>
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink">{children}</dd>
    </div>
  );
}
